// RUTA: src/hooks/useCoachInfo.ts
//
// Resuelve la info del coach asignado a un cliente: id, nombre y
// avatar. Usado por las cards de racha para mostrar la cara del
// coach hablándole al cliente con el mensaje motivacional.
//
// Si el cliente tiene varios coaches, devolvemos el primero (los
// arreglos múltiples son raros — un cliente normalmente tiene 1
// coach principal).
//
// Si no tiene coach (admin sin clientes asignados), devuelve null y
// el componente cae a un mensaje genérico sin avatar.

import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getClientDisplayName } from '@/lib/clientDisplay';
import { getPrimaryCoachId } from '@/lib/primaryCoach';

export interface CoachInfo {
  id: string;
  name: string;
  avatarUrl: string | null;
}

export function useCoachInfo(clientId: string | null | undefined) {
  const [coach, setCoach] = useState<CoachInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientId) {
      setCoach(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);

    (async () => {
      const coachId = await getPrimaryCoachId(clientId);
      if (cancelled) return;
      if (!coachId) {
        setCoach(null);
        setLoading(false);
        return;
      }

      // Profile + avatar del coach (vienen de tablas distintas — el
      // avatar y el "full_name" del onboarding coach están en
      // client_onboarding_profiles incluso para coaches).
      const [{ data: profile }, { data: onboarding }] = await Promise.all([
        supabase.from('profiles').select('display_name').eq('id', coachId).maybeSingle(),
        supabase
          .from('client_onboarding_profiles')
          .select('full_name, avatar_url, email')
          .eq('user_id', coachId)
          .maybeSingle(),
      ]);

      if (cancelled) return;

      const name = getClientDisplayName(profile, onboarding);
      setCoach({
        id: coachId,
        name,
        avatarUrl: onboarding?.avatar_url || null,
      });
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [clientId]);

  return { coach, loading };
}
